import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback
} from 'react';
import { useAssetMVPermissions, useTypedSelector } from '@hooks';
import type { fabric } from 'fabric';
import { useOutletContext } from 'react-router-dom';
import CommentEditor from '@pages/MediaViewer/Comments/components/CommentEditor';
import type { SubmitEditorValues } from '@pages/MediaViewer/Comments/components/CommentEditor/CommentEditor';
import {
  AssetItemDto,
  AssetVersionItemDto,
  MemberDto,
  TaskBoardItemDto
} from '@api/Api';
import { createNewComment } from '@redux/actions/mediaViewerAction';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import { useAuth } from '@hooks/useAuth';
import {
  setAssetListNeedRefresh,
  setListsNeedRefreshInCampaign
} from '@redux/reducers/tasks';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { useCreateTask } from '@hooks/tasks';
import { useFetch } from '@hooks/useFetch';
import {
  assetStateSelector,
  boardsSelector,
  campaignStateSelector
} from '@redux/selectors/tasks';
import {
  fetchAssetAssignees,
  fetchCampaignTaskBoards
} from '@redux/actions/tasks';
import { FabricContext } from '@context/FabricContext';
import useHasAnnotations from '@pages/MediaViewer/AssetViewer/Canvas/hooks/useHasAnnotations';
import { useMVCommentAttachUppy } from '@hooks/useMVCommentAttachUppy';
import { MediaViewerOutletContextState } from '@layouts/types';
import CommentTask from '@pages/MediaViewer/Comments/components/CommentTask';
import {
  buildCreateCommentPayload,
  createTurnCommentIntoTask,
  mapUppyFilesToAttachments
} from '@pages/MediaViewer/helpers/comment';
import { useCommentMentions } from '@pages/MediaViewer/hooks/useComment';
import getObjectViewportPosition, {
  choosePosition,
  constrainPositionToWindow,
  getObjectViewportRect
} from '@pages/MediaViewer/AssetViewer/Canvas/utils/getObjectViewportPosition';

import './FloatCommentEditor.less';

type FloatCommentEditorProps = {
  selectedVersion: AssetVersionItemDto;
  asset: AssetItemDto;
  canCreateTask: boolean;
  commentMentions: boolean;
};

function FloatCommentEditor({
  canCreateTask,
  commentMentions,
  asset,
  selectedVersion
}: FloatCommentEditorProps) {
  const { addNewComment, getPotentialMentions, getPotentialGroupMentions } =
    useOutletContext<MediaViewerOutletContextState>();
  const { clearCanvas, canvas } = useContext(FabricContext);
  const { user } = useAuth();
  const dispatch = useTypedDispatch();
  const createTask = useCreateTask();
  const hasAnnotations = useHasAnnotations();
  const canViewTasks = useAssetMVPermissions('listTasks');
  const { campaignId, workspaceId, id: assetId } = asset;
  const uppy = useMVCommentAttachUppy(assetId);
  const {
    activePageNumber,
    playerTime,
    selectedCommentId,
    setDrawToolsShape,
    selectComment,
    setCanvasMode,
    setDocumentHighlight,
    canvasMode,
    documentHighlight
  } = useAssetViewerState();
  const [isShowTask, setIsShowTask] = useState<boolean>(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState<boolean>(true);
  const [selectedUser, setSelectedUser] = useState<MemberDto | null>(null);
  const [selectedBoard, setSelectedBoard] = useState<TaskBoardItemDto | null>(
    null
  );
  const assetType = selectedVersion?.metadata.type;
  const isAudio = assetType === 'video' || assetType === 'audio';
  const isDocument =
    assetType === 'document' ||
    assetType === 'presentation' ||
    assetType === 'spreadsheet';
  const checkboxValueType =
    (isDocument && 'pageNumber') || (isAudio && 'timeCode') || null;

  const potentialAssignees = useTypedSelector(
    (state) => assetStateSelector(state, { assetId }).potentialAssignees.list
  );
  const taskBoards = useTypedSelector((state) =>
    boardsSelector(state, { campaignId })
  ).filter((x) => x.id !== 'archived' && x.id !== 'no_assigned');

  useFetch({
    key: `campaign-task-board-${campaignId}`,
    disabled: user?.type !== 'internal' || !campaignId || !canViewTasks,
    selector: (state) =>
      campaignStateSelector(state, { campaignId }).boards.fetch,
    fetch: (fetchType) => {
      return dispatch(
        fetchCampaignTaskBoards({
          campaignId,
          fetchType
        })
      );
    }
  });
  useEffect(() => {
    dispatch(setListsNeedRefreshInCampaign({ campaignId }));
  }, [campaignId, dispatch]);

  useFetch({
    key: `asset-assignees-${assetId}`,
    disabled: user?.type !== 'internal' || !assetId || !canViewTasks,
    selector: (state) =>
      assetStateSelector(state, { assetId }).potentialAssignees.fetch,
    fetch: (fetchType) => {
      return dispatch(
        fetchAssetAssignees({
          workspaceId,
          assetId,
          fetchType
        })
      );
    }
  });

  useEffect(() => {
    dispatch(setAssetListNeedRefresh({ assetId }));
  }, [assetId, dispatch]);

  const onTurnCommentIntoTask = createTurnCommentIntoTask({
    asset,
    user,
    createTask,
    dispatch,
    selectedBoardId: selectedBoard?.id,
    selectedUser
  });

  const onCancel = () => {
    clearCanvas();
    setDrawToolsShape('cursor');
    setCanvasMode('view');
    selectComment(null);
    setDocumentHighlight(null);
  };

  const onAddComment = async ({ ...values }: SubmitEditorValues) => {
    const data = buildCreateCommentPayload({
      values,
      selectedVersionId: selectedVersion?.id || '',
      isCheckboxChecked,
      isAudioVideo: isAudio,
      isDocument,
      playerTime,
      activePageNumber,
      annotations: getAnnotations()
    });
    const comment = await addNewComment(data, '');
    const files = uppy.getFiles();
    if (files.length) {
      files.forEach((item) => {
        uppy.setFileMeta(item.id, {
          commentId: comment.id,
          readyForUpload: true
        });
      });
      await uppy.upload();
    }
    await createNewComment(
      {
        ...comment,
        attachments: mapUppyFilesToAttachments(files)
      },
      selectedVersion?.id || ''
    );
    onCancel();
    if (isShowTask) {
      onTurnCommentIntoTask(comment);
    }
  };

  const getAnnotations = (): string | null => {
    if (!canvas) return null;
    const json = canvas.toJSON(['assetAngle', 'userEmail', 'userId']) as any;
    const getObjects = json.objects.filter(
      (item: fabric.Object) => item.type !== 'highlight'
    );
    if (!getObjects.length || !hasAnnotations) return null;
    return JSON.stringify(getObjects);
  };

  const { potentialMentions, potentialGroupMentions, setSearchQuery } =
    useCommentMentions({
      assetVersionId: selectedVersion?.id,
      getPotentialMentions,
      getPotentialGroupMentions
    });

  const [hasActiveObject, setHasActiveObject] = useState(false);
  const [position, setPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);

  const updateActiveObjectState = useCallback(() => {
    if (!canvas) return;
    if (canvasMode === 'view') return;
    const drawableObjects = canvas
      .getObjects()
      .filter((obj) => obj.type !== 'highlight' && obj.visible !== false);
    const activeObject = canvas.getActiveObject();
    const anchorObject = activeObject || drawableObjects[0];
    setHasActiveObject(!!anchorObject);
    if (anchorObject) {
      const initialPos = getObjectViewportPosition(canvas, anchorObject);
      const constrained = constrainPositionToWindow(initialPos);
      const objectRect = getObjectViewportRect(canvas, anchorObject);
      let pos: { left: number; top: number } | null = constrained;
      if (objectRect && constrained) {
        pos = choosePosition(canvas, objectRect, constrained) ?? constrained;
      }
      setPosition(pos);
    } else {
      setPosition(null);
    }
  }, [canvas, canvasMode]);

  useEffect(() => {
    if (!canvas) return;
    updateActiveObjectState();
    canvas.on('selection:created', updateActiveObjectState);
    canvas.on('selection:updated', updateActiveObjectState);
    canvas.on('selection:cleared', updateActiveObjectState);
    canvas.on('object:modified', updateActiveObjectState);
    canvas.on('object:deleted', updateActiveObjectState);
    return () => {
      canvas.off('selection:created', updateActiveObjectState);
      canvas.off('selection:updated', updateActiveObjectState);
      canvas.off('selection:cleared', updateActiveObjectState);
      canvas.off('object:modified', updateActiveObjectState);
      canvas.off('object:deleted', updateActiveObjectState);
    };
  }, [canvas, updateActiveObjectState]);

  const positionFromDocumentHighlight = useMemo(() => {
    if (canvasMode === 'view') return null;
    if (!canvas) return null;
    if (!documentHighlight?.text) return null;
    if (!documentHighlight?.rects?.length) return null;
    const canvasEl = canvas.getElement();
    if (!canvasEl) return null;
    const canvasRect = canvasEl.getBoundingClientRect();
    const lastRect =
      documentHighlight.rects[documentHighlight.rects.length - 1];
    const GAP = 8;
    const left = canvasRect.left + (lastRect.left / 100) * canvasRect.width;
    const top =
      canvasRect.top +
      ((lastRect.top + lastRect.height) / 100) * canvasRect.height +
      GAP;
    return constrainPositionToWindow({ left, top });
  }, [canvas, documentHighlight?.text, documentHighlight?.rects, canvasMode]);

  const showForActiveObject =
    hasActiveObject && position && selectedCommentId === 'new';
  const showForDocumentHighlight =
    !!documentHighlight?.text &&
    !!documentHighlight?.rects?.length &&
    positionFromDocumentHighlight;

  if (!showForActiveObject && !showForDocumentHighlight) return null;

  const editorPosition = showForActiveObject
    ? position
    : positionFromDocumentHighlight;

  return (
    <div
      className="float_comment_editor"
      style={{
        left: editorPosition!.left,
        top: editorPosition!.top
      }}
    >
      <CommentEditor
        commentMentions={commentMentions}
        editCommentId="new"
        setEditCommentId={() => undefined}
        editorFor="new"
        autoFocus={false}
        isFocused={true}
        replyToCommentId={null}
        setReplyToCommentId={() => undefined}
        onSubmitEditor={onAddComment}
        isCheckboxChecked={isCheckboxChecked}
        setIsCheckboxChecked={setIsCheckboxChecked}
        checkboxValueType={checkboxValueType}
        potentialMentions={potentialMentions}
        potentialGroupMentions={potentialGroupMentions}
        searchPotentialMentions={(v) => setSearchQuery(v)}
        uppy={uppy}
      />
      <CommentTask
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
        selectedBoard={selectedBoard}
        setSelectedBoard={setSelectedBoard}
        isShowTask={isShowTask}
        setIsShowTask={setIsShowTask}
        potentialAssignees={potentialAssignees}
        boardList={
          selectedVersion?.versionNumber === asset?.asset?.versionsCount
            ? taskBoards
            : []
        }
        canCreateTask={canCreateTask && user?.type === 'internal'}
        leftSlot={
          <div
            className="float_comment_editor_footer_cancel"
            onClick={onCancel}
          >
            Cancel
          </div>
        }
      />
    </div>
  );
}

export default FloatCommentEditor;
